import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight, Droplets, Sparkles, TrendingUp } from "lucide-react";
import { ASSETS, PRODUCTS } from "@/data/site";
import { MaskedLine, Reveal, Overline } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import ProductCard from "@/components/ProductCard";
import { useState } from "react";
import PricingModal from "@/components/PricingModal";

const EASE = [0.22, 1, 0.36, 1];

function TiltCard({ children, className }) {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-6, 6]), { stiffness: 120, damping: 18 });

  return (
    <motion.div
      className={className}
      style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
      }}
      onMouseLeave={() => {
        mx.set(0);
        my.set(0);
      }}
    >
      {children}
    </motion.div>
  );
}

const CHAPTERS = [
  {
    n: "01",
    icon: Droplets,
    title: "Purity First",
    body: "Every bottle of Ananya Refresh is RO-purified, mineral-rich and batch-tested. For a retailer, purity is not a promise — it is repeat business.",
  },
  {
    n: "02",
    icon: Sparkles,
    title: "Taste Engineering",
    body: "Masala Blast and our namkeen range are built on sensory-driven quality — bold seasoning, perfect crunch, and flavours tuned for the Indian palate.",
  },
  {
    n: "03",
    icon: TrendingUp,
    title: "Distributor Growth",
    body: "High-volume retail formats, the unbeatable ₹5 price point and dependable supply mean your shelves never sit still.",
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yWater = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const yChips = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const [pricingFor, setPricingFor] = useState(null);

  const featured = PRODUCTS.filter((p) => ["water-1l", "masala-blast-kurkure", "masala-blast-chips", "all-in-one"].includes(p.id));

  return (
    <div>
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden pt-32 pb-20">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_75%_20%,rgba(132,204,22,0.10),transparent_65%)]" />
        <div className="mx-auto max-w-6xl px-6 grid gap-16 lg:grid-cols-[1.1fr_1fr] items-center relative">
          <div>
            <Reveal delay={0.1}>
              <div className="flex flex-wrap gap-2 mb-8">
                <span data-testid="hero-badge-retail" className="rounded-full border border-lime/40 bg-lime/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-lime">
                  High-Volume Retail
                </span>
                <span data-testid="hero-badge-quality" className="rounded-full border border-sun/40 bg-sun/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-sun-bright">
                  Sensory-Driven Quality
                </span>
              </div>
            </Reveal>
            <h1 data-testid="hero-headline" className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02]">
              <MaskedLine delay={0.15}>Pure Hydration.</MaskedLine>
              <MaskedLine delay={0.3} className="text-sun-bright">Bold Masala.</MaskedLine>
              <MaskedLine delay={0.45}><span className="text-stroke-lime">Built for Volume.</span></MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
              className="mt-7 max-w-lg text-base leading-relaxed text-slate-300"
            >
              Ananya Refresh pairs RO-pure packaged drinking water with a ₹5 snack range that flies off
              shelves. We are onboarding distributors and shop owners who want dependable supply and
              serious margins.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
              className="mt-9 flex flex-wrap gap-4"
            >
              <Link
                to="/partnership"
                data-testid="hero-request-quote-btn"
                className="inline-flex items-center gap-2 rounded-full bg-lime px-7 py-4 text-sm font-bold text-navy transition-all duration-300 hover:bg-sun-bright hover:gap-3"
              >
                Become a Distributor <ArrowUpRight size={17} />
              </Link>
              <Link
                to="/products"
                data-testid="hero-explore-btn"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-4 text-sm font-bold text-white transition-colors duration-300 hover:border-lime hover:text-lime"
              >
                Explore the Range
              </Link>
            </motion.div>
          </div>

          <div className="relative" style={{ perspective: 1000 }}>
            <motion.div style={{ y: yWater }} initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.1, delay: 0.4, ease: EASE }}>
              <TiltCard className="spotlight-card rounded-3xl border border-white/10 p-3 shadow-2xl shadow-black/50">
                <img
                  data-testid="hero-water-image"
                  src={ASSETS.waterBanner}
                  alt="Ananya Refresh packaged drinking water — 1L, 500ml, 200ml"
                  className="rounded-2xl w-full object-cover"
                />
              </TiltCard>
            </motion.div>
            <motion.div
              style={{ y: yChips }}
              initial={{ opacity: 0, y: 60, rotate: -6 }}
              animate={{ opacity: 1, y: 0, rotate: -6 }}
              transition={{ duration: 1, delay: 0.9, ease: EASE }}
              className="absolute -bottom-14 -left-6 sm:-left-14 w-36 sm:w-48 rounded-2xl bg-sun p-2 shadow-xl shadow-black/40"
            >
              <img data-testid="hero-chips-orange" src={ASSETS.chipsOrange} alt="Masala Blast wavy chips" className="rounded-xl w-full object-cover" />
            </motion.div>
            <motion.div
              style={{ y: yChips }}
              initial={{ opacity: 0, y: 60, rotate: 7 }}
              animate={{ opacity: 1, y: 0, rotate: 7 }}
              transition={{ duration: 1, delay: 1.05, ease: EASE }}
              className="absolute -top-10 -right-4 sm:-right-10 w-32 sm:w-44 rounded-2xl bg-lime p-2 shadow-xl shadow-black/40"
            >
              <img data-testid="hero-chips-green" src={ASSETS.chipsGreen} alt="Masala Blast kurkure" className="rounded-xl w-full object-cover" />
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee
        items={[
          "Reliable Supply Chain",
          "High Margin Retail",
          "100% Purity Guaranteed",
          "₹5 Power Price Point",
          "Nationwide Logistics",
        ]}
      />

      <section className="mx-auto max-w-6xl px-6 pt-32">
        <Reveal>
          <Overline>The Manifesto</Overline>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-2xl">
            Three reasons retailers stock Ananya Refresh.
          </h2>
        </Reveal>
        <div className="mt-16 grid gap-6 md:grid-cols-3">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.12} data-testid={`manifesto-chapter-${i + 1}`}>
              <div data-testid={`manifesto-chapter-${i + 1}`} className="group h-full rounded-3xl border border-white/10 bg-navy-card p-8 transition-all duration-500 hover:border-sun/50 hover:-translate-y-2">
                <div className="flex items-start justify-between">
                  <span className="font-display text-5xl font-extrabold text-stroke-lime">{c.n}</span>
                  <c.icon size={26} className="text-sun-bright transition-transform duration-500 group-hover:scale-125 group-hover:rotate-12" />
                </div>
                <h3 className="mt-8 font-display text-xl font-bold">{c.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pt-32">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Overline>Featured Range</Overline>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
              Shelf-ready. Margin-friendly.
            </h2>
          </div>
          <Link
            to="/products"
            data-testid="home-view-all-products"
            className="inline-flex items-center gap-2 text-sm font-bold text-lime hover:text-sun-bright transition-colors"
          >
            View full catalogue <ArrowUpRight size={16} />
          </Link>
        </Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} onRequest={setPricingFor} />
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pt-32" data-testid="home-gold-section">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2.5rem] border border-sun/25 bg-gradient-to-br from-navy-card to-navy-deep px-8 py-14 sm:px-14">
            <div className="absolute -left-24 -top-24 h-80 w-80 rounded-full bg-sun/15 blur-3xl" />
            <div className="relative grid gap-12 lg:grid-cols-[1.1fr_1fr] items-center">
              <div>
                <span className="inline-block bg-white rounded-2xl px-5 py-3 shadow-xl">
                  <img src={ASSETS.logoGold} alt="Ananya Gold" className="h-12 w-auto" />
                </span>
                <Overline className="mt-8 !text-sun-bright">Our Sister Brand</Overline>
                <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight">
                  Ananya Gold — personal &amp; home care by Ananya Consumer Goods Pvt. Ltd.
                </h2>
                <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-300">
                  Germ-defence soaps, glycerin bars, liquid hand wash and powerful home-care
                  essentials — quality products from small manufacturers, repackaged and branded
                  under one trusted label.
                </p>
                <div className="mt-8 flex flex-wrap gap-4">
                  <Link
                    to="/products?brand=gold"
                    data-testid="home-gold-explore-btn"
                    className="inline-flex items-center gap-2 rounded-full bg-sun-bright px-7 py-4 text-sm font-bold text-navy transition-all duration-300 hover:bg-lime hover:gap-3"
                  >
                    Explore Ananya Gold <ArrowUpRight size={16} />
                  </Link>
                  <Link
                    to="/consumer-goods"
                    data-testid="home-gold-company-btn"
                    className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-4 text-sm font-bold text-white transition-colors duration-300 hover:border-sun-bright hover:text-sun-bright"
                  >
                    About the Company
                  </Link>
                </div>
              </div>
              <div className="flex items-end justify-center gap-4">
                <img
                  data-testid="home-gold-img-soap"
                  src="/assets/gold-soap-original.jpg"
                  alt="Ananya Gold Original Germ Defence soap"
                  className="w-1/3 rounded-2xl border border-white/10 shadow-xl shadow-black/40 -rotate-3 transition-transform duration-500 hover:rotate-0 hover:-translate-y-2"
                  loading="lazy"
                />
                <img
                  data-testid="home-gold-img-handwash"
                  src="/assets/gold-handwash.jpg"
                  alt="Ananya Gold liquid hand wash collection"
                  className="w-2/5 rounded-2xl border border-white/10 shadow-xl shadow-black/40 transition-transform duration-500 hover:-translate-y-2"
                  loading="lazy"
                />
                <img
                  data-testid="home-gold-img-cleaner"
                  src="/assets/gold-toilet-cleaner.jpg"
                  alt="Ananya Gold toilet cleaner"
                  className="w-1/3 rounded-2xl border border-white/10 shadow-xl shadow-black/40 rotate-3 transition-transform duration-500 hover:rotate-0 hover:-translate-y-2"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-6xl px-6 pt-32">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2.5rem] bg-lime px-8 py-16 sm:px-16 text-navy">
            <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-sun-bright/50 blur-3xl" />
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight max-w-2xl leading-tight">
              Your territory. Our supply. One call away.
            </h2>
            <p className="mt-5 max-w-xl text-base font-medium text-navy/80">
              Join the Ananya Refresh distribution network and get market support, reliable logistics and
              a product range customers already ask for by name.
            </p>
            <Link
              to="/partnership"
              data-testid="home-partnership-cta"
              className="mt-9 inline-flex items-center gap-2 rounded-full bg-navy px-8 py-4 text-sm font-bold text-white transition-all duration-300 hover:gap-3 hover:bg-navy-deep"
            >
              Start the Conversation <ArrowUpRight size={17} />
            </Link>
          </div>
        </Reveal>
      </section>

      <PricingModal product={pricingFor} open={!!pricingFor} onOpenChange={(o) => !o && setPricingFor(null)} />
    </div>
  );
}
